import { useQuery } from '@tanstack/react-query';
import { createSubscriptionQuery } from 'shared/lib/@tanstack-query';
import { GetGoalsOptions, getGoalsQuery } from 'shared/services/backend-api';
import { ID } from 'shared/types/id';
import { chunkArray } from 'shared/utils/chunk-array';

export const queryKey = 'goals';

export const useGoalsQuery = (
  uid?: ID,
  options: GetGoalsOptions = {},
  enabled = true,
) =>
  useQuery({
    queryKey: [
      queryKey,
      uid ?? '',
      enabled.toString(),
      ...Object.entries(options)
        .filter(([, value]) => value !== undefined)
        .map(
          ([key, value]) =>
            `${key}: ${
              value instanceof Date
                ? value.getTime().toString()
                : Array.isArray(value)
                  ? value.toSorted().join(',')
                  : value?.toString()
            }`,
        ),
    ],
    queryFn: (context) => {
      if (!enabled) {
        return [];
      }

      const { ids, lifeAreas, ...rest } = options;
      let queries = [() => getGoalsQuery(options)];

      if (lifeAreas?.length && lifeAreas.length > 30) {
        // chunk to max-length of 30 since firebase doesn't accept more
        const chunkedLifeAreas = chunkArray(lifeAreas.toSorted(), 30);
        queries = chunkedLifeAreas.map(
          (chunked) => () =>
            getGoalsQuery({ ...rest, ids, lifeAreas: chunked }),
        );
      }

      if (ids?.length && ids.length > 30) {
        // chunk to max-length of 30 since firebase doesn't accept more
        const chunkedIds = chunkArray(ids.toSorted(), 30);
        queries = chunkedIds.map(
          (chunked) => () =>
            getGoalsQuery({ ...rest, lifeAreas, ids: chunked }),
        );
      }

      return createSubscriptionQuery(queries)(context);
    },
    enabled: !!uid && enabled,
  });

export type { GetGoalsOptions };
